/* eslint-disable import/no-unresolved */
import _ from 'underscore';
import {
  DeterministicView,
  helpers,
  type DeterministicViewInstance,
} from '@bingads-webui/deterministic-view';
import AlertView from '../alert/index';
import template from './template.pug';
import {
  IAlert, IAlertListViewOptions, IAlertListViewState,
} from '../types';

const { updateView, ensureChildView } = helpers;

type AlertListViewConstructor = new (
  options?: IAlertListViewOptions
) => DeterministicViewInstance<IAlertListViewState>;

/**
 * Represents a list of alerts, plus an editor to add/change alerts
 */
export default class AlertListView extends (DeterministicView as AlertListViewConstructor) {
  initialize({ level }: IAlertListViewOptions) {
    this.$el.attr({
      'data-level': level,
    });
    this.setState();
  }

  /**
   * Propagate the state to model and children views.
   * @param {IAlertListViewState} state - The new state
   * @override
   * @returns {AlertListView} - The view itself
   */
  setState({ alertIds = [], alerts = {} }: IAlertListViewState = { alertIds: [], alerts: {} }) {
    _.each(alertIds, (alertId: string) => {
      ensureChildView(this, `alert(${alertId})`, AlertView, {}, {
        ignore: () => {
          this.trigger('ignore', {
            id: alertId,
            alert: alerts[alertId],
          });
          this.dismiss(alertId);
        },
      }).setState(alerts[alertId]);
    });

    return super.setState({ alertIds });
  }

  /**
   * Collect the state from model and children views.
   * @override
   * @returns {IAlertListViewState} - The state of the view.
   */
  getState(): IAlertListViewState {
    const { alertIds = [] } = super.getState();

    return {
      alertIds,
      alerts: _.object(alertIds, _.map(
        alertIds,
        (key: string) => (this.getChild(`alert(${key})`) as DeterministicViewInstance<IAlert>).getState()
      )),
    };
  }

  static newId() {
    return _.uniqueId('alert-');
  }

  // TODO: [#591704] after Services/AlertService is refactored, ID needs validation to make
  // sure it's generated by this component to avoid collision
  show(alert: IAlert, id: string) {
    const { alertIds, alerts } = this.getState();

    this.setState({
      // keep tracking the alert show order by time through the alert Id
      alertIds: _.union(alertIds, [id]),
      alerts: _.defaults({
        [id]: alert,
      }, alerts),
    });

    return id;
  }

  dismiss(alertId: string) {
    const { alertIds } = this.getState();

    updateView(this, {
      alertIds: _.without(alertIds, alertId),
    });
  }

  dismissAll() {
    const { alertIds } = this.getState();

    _.each(alertIds, (id: string) => this.dismiss(id));
  }

  get className() {
    return 'alert-center__level';
  }

  get attributes() {
    return {
      role: 'group',
    };
  }

  get tagName() {
    return 'ol';
  }

  get template() {
    return template;
  }
}
